import { chromeWord } from '@shared/branding';
import { useBranding } from '../state';

/**
 * "cloud." in Baloo 800 with a teal stop.
 *
 * The lockup used to read "mauvely." at 800 followed by "cloud" at 400. That is
 * the *website's* lockup, and it was wrong here: Compose's title bar says
 * `compose.` and Snap's says `snap.`, so the web app was the one product in the
 * suite introducing itself by company name. One word, one stop, same as its
 * siblings.
 *
 * Cloud does not use the design system's `Wordmark` because that component
 * cannot express the two-tone stop. Baloo 2 is vendored (`scripts/fonts.mjs`)
 * rather than loaded from a CDN, because the CSP on this origin names no host
 * but our own; see docs/SECURITY.md.
 *
 * This lived in two places — the shell chrome and the public share page, which
 * renders with no shell at all — and the two had drifted to different sizes and
 * a different gap. One component, two sizes, one lockup.
 *
 * The optional product tile is the real mark from `Mauvely/resources`, copied
 * verbatim to `web/public/brand/cloud.svg`. **Collapsing the rail leaves the
 * tile, not a letter.** It used to leave a bare `c.`, on the reasoning that
 * 76px was already carrying the `c` — but a single letter and a stop is not a
 * mark, it is the lockup with most of it missing, and it reads as a rendering
 * fault rather than as a deliberate compact state. The tile is the same size
 * either way, so collapsing removes the word and moves nothing.
 *
 * ── Under an organisation's branding ────────────────────────────────────────
 * The word comes from the organisation when it has set one, and the tile from
 * its logo when it has uploaded one. Both fall back to Mauvely's, which is what
 * every account that is in no organisation gets.
 *
 * **The stop stays teal.** It is the mark's one flash of signal colour and one
 * of teal's two sanctioned uses; it is what makes this read as a lockup rather
 * than as display type. An organisation that changed only its accent should not
 * find it has also changed the punctuation.
 */

interface Props {
  /** Rail state. Collapsed drops the word and keeps the tile — or, with no
   *  tile to keep, the mark's first letter and its stop. */
  collapsed?: boolean;
  /** Height of the word in px. */
  size?: number;
  /** Draw the cloud product tile ahead of the mark. */
  showMark?: boolean;
}

export function Wordmark({ collapsed = false, size = 21, showMark = false }: Props) {
  const display = "var(--font-display), 'Baloo 2', system-ui";
  const branding = useBranding();
  const word = chromeWord(branding, 'cloud');

  const mark = (
    <img
      src={branding?.logoUrl ?? '/brand/cloud.svg'}
      alt=""
      aria-hidden="true"
      width={size + 5}
      height={size + 5}
      // `alignSelf` because the tile is a box in a baseline-aligned row:
      // left to the baseline it hangs below the text it sits beside.
      // `objectFit` because a customer's logo is whatever shape they
      // uploaded, and the tile is square — letterboxing it keeps the row
      // metrics that everything beside it is aligned to.
      style={{
        alignSelf: 'center',
        borderRadius: 6,
        flex: 'none',
        display: 'block',
        objectFit: 'contain'
      }}
    />
  );

  // Collapsed with a tile: the tile alone, at the size it already was. The
  // word is what goes away, not the mark. `aria-hidden` comes off here because
  // this is now the only thing naming the destination — the expanded lockup
  // has the word beside it to do that.
  if (collapsed && showMark) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center' }} title={word}>
        <img
          src={branding?.logoUrl ?? '/brand/cloud.svg'}
          alt={word}
          width={size + 5}
          height={size + 5}
          style={{
            borderRadius: 6,
            flex: 'none',
            display: 'block',
            objectFit: 'contain'
          }}
        />
      </span>
    );
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: showMark && !collapsed ? 9 : 0 }}>
      {showMark && !collapsed && mark}
      <span style={{ fontFamily: display, fontWeight: 800, fontSize: size, color: 'var(--text-strong)' }}>
        {collapsed ? word.slice(0, 1) : word}
        <span style={{ color: 'var(--signal)' }}>.</span>
      </span>
    </span>
  );
}
